import {
  Component,
  Input,
  OnInit,
  EventEmitter,
  Output,
  OnChanges,
  SimpleChanges,
  OnDestroy,
} from '@angular/core';

import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';

import { TranslocoService } from '@jsverse/transloco';
import { Subject, combineLatest } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

import { urlValidator } from 'app/modules/configuration/configuration-utils';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';

@EnableClassTelemetry()
@Component({
  selector: 'app-response-type-form',
  templateUrl: './response-type-form.component.html',
  styleUrls: ['./response-type-form.component.scss'],
  standalone: false,
})
export class ResponseTypeFormComponent implements OnInit, OnChanges, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() responseType: string;
  @Input() callbackUrl?: string;
  @Input() hasAutoAnalysis?: boolean;
  @Input() hasMachineTranslation?: boolean;
  @Input() canEditRoute: boolean;

  @Output() responseTypeChange = new EventEmitter<string>();
  @Output() callbackUrlChange = new EventEmitter<string | undefined>();
  @Output() validityChange = new EventEmitter<boolean>();

  form: UntypedFormGroup;

  unsub$ = new Subject<void>();

  responseTypeTooltip;
  autoResponseTooltip;

  constructor(
    private translocoService: TranslocoService,
    private formBuilder: UntypedFormBuilder
  ) {
    this.form = this.formBuilder.group({
      responseType: [this.responseType],
      callbackUrl: [this.callbackUrl, Validators.compose([Validators.required, urlValidator])],
    });
  }

  ngOnInit(): void {
    this.form.valueChanges.subscribe(() => {
      this.responseTypeChange.emit(this.form.controls.responseType.value);
      this.callbackUrlChange.emit(this.form.controls.callbackUrl.value);
    });

    this.form.statusChanges.pipe(distinctUntilChanged()).subscribe((status) => {
      this.validityChange.emit(status !== 'INVALID');
    });

    combineLatest([
      this.translocoService.selectTranslate(
        'configuration-routes.view.settings-tab.auto-analysis-tooltip'
      ),
      this.translocoService.selectTranslate(
        'configuration-routes.view.settings-tab.machine-translation-tooltip'
      ),
    ])
      .pipe(takeUntil(this.unsub$))
      .subscribe((translations) => this.updateTooltips(...translations));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.responseType) {
      this.form.controls.responseType.setValue(this.responseType);
      this.onResponseTypeUpdated();
    }
    if (changes.callbackUrl) {
      this.form.controls.callbackUrl.patchValue(this.callbackUrl);
    }

    this.updateTooltips(
      this.translocoService.translate(
        'configuration-routes.view.settings-tab.auto-analysis-tooltip'
      ),
      this.translocoService.translate(
        'configuration-routes.view.settings-tab.machine-translation-tooltip'
      )
    );
  }

  ngOnDestroy(): void {
    this.unsub$.next();
    this.unsub$.complete();
  }

  private updateTooltips(responseType, autoResponse) {
    this.responseTypeTooltip = undefined;
    this.autoResponseTooltip = undefined;

    if (this.hasAutoAnalysis) {
      this.responseTypeTooltip = responseType;
    }

    if (this.hasMachineTranslation) {
      this.autoResponseTooltip = autoResponse;
    }
  }

  onResponseTypeUpdated() {
    if (
      this.form.controls.responseType.value === 'POLLING' ||
      this.form.controls.responseType.value === 'AUTORESPONSE'
    ) {
      this.form.controls.callbackUrl.disable();
    } else {
      this.form.controls.callbackUrl.enable();
    }
  }
}
